import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import ReusableButton from './Button/Button';
import CrossIcon from '../../assets/CrossIcon';
import CheckIcon from '../../assets/CheckIcon';
import RedCrossIcon from '../../assets/icon/RedCrossIcon';
import { translate } from '../../i18n';
import { ICancellationReason } from '../../type/cancelReason';
import ReusableSelect from './Select';
import { ICancelFlight } from '../../type/cancelFlight';
import { toast } from 'react-toastify';
import operationAPI from '../../api/operationApi';
import { CUSTOM_TOAST_ID } from '../../constants/constant';
import { showOrReplaceToast } from '../../utils/utility';
import CrossIconDarkForm from '../../assets/icon/CrossIconForm';

interface CancellationFormProps {
  flightNumber: number;
  cancellationReasons: ICancellationReason[];
  capacity: number;
  operationBalloonId: number;
  operationBalloonFlightId: number;
  closeModal: () => void;
  onConfirm: (data: any) => void;
}

const CancellationForm: React.FC<CancellationFormProps> = ({
  flightNumber,
  cancellationReasons,
  operationBalloonId,
  operationBalloonFlightId,
  capacity,
  closeModal,
  onConfirm,
}) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      reasonId: '',
      comments: '',
    },
  });

  const onSubmit = async (data: any) => {
    const payload: ICancelFlight = {
      comment: data.comments || '',
      reasonId: data.reasonId,
      operationBalloonId,
      operationBalloonFlightId,
      cancel: true,
    };

    try {
      await operationAPI.getCancelFlight(payload);
      closeModal();
      onConfirm(payload);
    } catch (error) {
      showOrReplaceToast(
        CUSTOM_TOAST_ID.flight,
        'Cancellation failed',
        'error',
      );
    }
  };

  return (
    <div className="max-w-md mx-auto bg-sidebar-background p-6 rounded-lg shadow-md relative">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-base font-semibold text-text-blue">
          {translate('flightForm.formTitle')}
        </h2>
        <button onClick={closeModal}>
          <RedCrossIcon />
        </button>
      </div>
      <div className="border-b-2 border-[#EEF7FF] mb-4"></div>

      <p className="text-sm font-normal text-text-primary text-start mb-4">
        {translate('flightForm.formSubTitle')}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-normal text-text-primary text-start">
              {translate('flightForm.flight')}
            </label>
            <input
              type="text"
              value={flightNumber}
              readOnly
              className="mt-1 block w-full p-2 text-xs border  border-customBorder-border rounded-md bg-gray-100 bg-backgroundColor-darkThemeColor text-text-secondary"
            />
          </div>
          <div>
            <label className="block text-sm font-normal text-text-primary text-start">
              {translate('flightForm.capacity')}
            </label>
            <input
              type="text"
              value={capacity}
              readOnly
              className="mt-1 block w-full p-2  text-xs border border-customBorder-border rounded-md bg-gray-100 bg-backgroundColor-darkThemeColor text-text-secondary"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-normal text-text-primary text-start">
            {translate('flightForm.reason')}
          </label>
          <Controller
            name="reasonId"
            control={control}
            rules={{ required: 'Reason is required' }}
            render={({ field }) => (
              <ReusableSelect
                options={cancellationReasons}
                getOptionLabel={(option) => option.reason}
                getOptionValue={(option) => option.id}
                value={field.value}
                onChange={(selected) => field.onChange(selected.id)}
                placeholder="Select Reason"
                className={`mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-primary   ${
                  errors.reasonId ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            )}
          />
          {errors.reasonId && (
            <p className="text-red-500 text-start text-xs mt-1">
              {errors.reasonId.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-normal text-text-secondary text-start">
            {translate('flightForm.commentsInput')}
          </label>
          <textarea
            {...register('comments')}
            rows={3}
            placeholder="Write here..."
            className="mt-1 block w-full p-2 border border-customBorder-border rounded-md bg-backgroundColor-darkThemeColor text-text-secondary"
          />
        </div>

        <div className="col-span-2 flex justify-between mt-4">
          <ReusableButton
            onClick={closeModal}
            variant="secondary"
            children={translate('common.cancel')}
            icon={
                  <div>
                    <div className="block dark:hidden">
                      <CrossIcon />
                    </div>
                    <div className="hidden dark:block">
                      <CrossIconDarkForm />
                    </div>
                  </div>
                }
            className="w-full bg-button-cancelButtonBgColor text-text-cancelButtonTextColor"
          />
          <ReusableButton
            icon={<CheckIcon />}
            children={translate('common.confirm')}
            variant="primary"
            className="w-full bg-background-backgroundColor"
          />
        </div>
      </form>
    </div>
  );
};

export default CancellationForm;
